import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CollectionChatSidebar
 *
 * A scoped chat surface that opens from a Collection's header as a
 * right-anchored sidebar — non-modal, so the user can keep scrolling /
 * clicking cards in the collection while talking. The backend
 * (`POST /v1/assistant/collection-chat`) is seeded with the Collection's
 * blocks, so every reply is grounded in the user's actual content.
 *
 * The transcript is persisted per-collection in localStorage so a
 * conversation survives reloads and tab switches. A trash button in the
 * header clears the persisted entry and resets the in-memory transcript.
 *
 * Server is stateless: we replay the last `MAX_HISTORY_REPLAY` turns on
 * every send.
 */
import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState, } from "react";
import { faCircleNotch, faPaperPlaneTop, faTrashCan, faXmark, } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { showToast } from "@shared/client/components/toast/show-toast";
import { chatWithCollection, } from "@/api/query-api";
import { blockStore } from "@/data/block-store";
import { clearCollectionChatMessages, loadCollectionChatMessages, saveCollectionChatMessages, } from "@/state/collection-chat-persistence";
import { useApp } from "@/state/app-context";
import "./collection-chat-sidebar.scss";
/**
 * Build the localStorage key suffix for a given chat surface.
 *
 * For a persisted Collection, the `collectionId` is stable across
 * sessions — perfect storage key. For an ad-hoc block set, we hash the
 * sorted ids so the transcript survives a page reload when the user
 * lands on the same search (deterministic same key) but doesn't bleed
 * across two different searches that happen to share a title. The
 * `qry-` prefix keeps these entries grouped + scannable in DevTools.
 *
 * `JSON.stringify(sorted)` produces a deterministic-but-long key; we
 * keep it short with a simple FNV-1a 32-bit hash, plenty of collision
 * resistance for a per-user localStorage namespace.
 */
function storageKeyForBlockIds(blockIds) {
    const sorted = [...blockIds].sort();
    let hash = 0x811c9dc5; // FNV offset basis
    for (const id of sorted) {
        for (let i = 0; i < id.length; i++) {
            hash ^= id.charCodeAt(i);
            hash = Math.imul(hash, 0x01000193) >>> 0;
        }
        hash ^= 0x2c; // ',' separator so [["a","b"], ["ab"]] don't collide
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return `qry-${hash.toString(36)}`;
}
/** Max prior turns we replay to the backend (mirrors `MAX_COLLECTION_CHAT_HISTORY`). */
const MAX_HISTORY_REPLAY = 20;
export const CollectionChatSidebar = memo((props) => {
    const { collectionTitle, onClose } = props;
    // Stable persistence key — `collectionId` for the persisted-collection
    // variant, an FNV-hash of the blockIds for the ad-hoc variant. The
    // existing `loadCollectionChatMessages` / `saveCollectionChatMessages`
    // helpers take any string, so all the swap-collection logic below
    // works the same way for both modes once we settle on this key.
    const storageKey = props.collectionId ?? storageKeyForBlockIds(props.blockIds);
    const textareaRef = useRef(null);
    const transcriptRef = useRef(null);
    const [draft, setDraft] = useState("");
    // Lazy initializer hydrates from localStorage on mount; the effect
    // below mirrors every subsequent change back to storage.
    const [messages, setMessages] = useState(() => loadCollectionChatMessages(storageKey));
    // Tracks which storage key the in-memory `messages` were hydrated from.
    // Used to guard the save effect against the brief window where
    // `storageKey` has changed but `setMessages(load(new))` hasn't yet
    // committed — without this, the old chat's transcript would get
    // written under the new chat's key for one render.
    const [hydratedFor, setHydratedFor] = useState(storageKey);
    const [isSending, setIsSending] = useState(false);
    // If the parent ever re-renders this sidebar with a different storage
    // key (collectionId change OR blockIds change for the ad-hoc variant),
    // swap to that surface's persisted transcript and drop the in-flight
    // draft.
    useEffect(() => {
        if (hydratedFor === storageKey)
            return;
        setMessages(loadCollectionChatMessages(storageKey));
        setDraft("");
        setHydratedFor(storageKey);
    }, [storageKey, hydratedFor]);
    // Persist on every change. The persistence layer caps array length, so
    // we can write unconditionally without debouncing. Gated on
    // `hydratedFor` so we never save the previous chat's messages under
    // the new chat's key (see comment on `hydratedFor`).
    useEffect(() => {
        if (hydratedFor !== storageKey)
            return;
        saveCollectionChatMessages(storageKey, messages);
    }, [storageKey, messages, hydratedFor]);
    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape")
                onClose();
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    useEffect(() => {
        if (typeof window !== "undefined" &&
            window.matchMedia("(max-width: 640px)").matches) {
            return;
        }
        textareaRef.current?.focus();
    }, []);
    // Pin the transcript to the bottom whenever a new turn lands. Using
    // useLayoutEffect avoids a flash where the user briefly sees the old
    // bottom before scrolling.
    useLayoutEffect(() => {
        const el = transcriptRef.current;
        if (!el)
            return;
        el.scrollTop = el.scrollHeight;
    }, [messages.length, isSending]);
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        const trimmed = draft.trim();
        if (trimmed.length === 0 || isSending)
            return;
        const userMessage = {
            id: `u-${Date.now()}`,
            role: "user",
            content: trimmed,
        };
        const history = messages
            .slice(-MAX_HISTORY_REPLAY)
            .map((m) => ({ role: m.role, content: m.content }));
        setMessages((prev) => [...prev, userMessage]);
        setDraft("");
        setIsSending(true);
        try {
            // Branch on invocation mode. The wire-side `chatWithCollection`
            // expects mutually-exclusive `collectionId` OR `blockIds+title`
            // shapes, so we build the right one from the props we got.
            const response = await chatWithCollection(props.collectionId !== undefined
                ? {
                    collectionId: props.collectionId,
                    message: trimmed,
                    history,
                }
                : {
                    blockIds: props.blockIds,
                    // The server uses this for the prompt's `<collection_title>`
                    // tag. For ad-hoc surfaces (smart-collection tabs), the
                    // assistant-query name reads naturally there.
                    title: collectionTitle,
                    message: trimmed,
                    history,
                });
            const assistantMessage = {
                id: `a-${Date.now()}`,
                role: "assistant",
                content: response.answer,
                referencedBlockIds: response.referencedBlockIds.length > 0
                    ? response.referencedBlockIds
                    : undefined,
            };
            setMessages((prev) => [...prev, assistantMessage]);
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : "Failed to send";
            showToast.error(msg);
            // Keep the user's message off the transcript and re-prime the
            // draft so they can retry without retyping.
            setDraft(trimmed);
            setMessages((prev) => prev.slice(0, -1));
        }
        finally {
            setIsSending(false);
        }
    }, [draft, isSending, messages, props, collectionTitle]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void handleSubmit(e);
        }
    }, [handleSubmit]);
    const handleClear = useCallback(() => {
        setMessages([]);
        clearCollectionChatMessages(storageKey);
        textareaRef.current?.focus();
    }, [storageKey]);
    const canClear = messages.length > 0 && isSending === false;
    return (_jsxs("aside", { className: "collection-chat-sidebar", role: "complementary", "aria-labelledby": "collection-chat-sidebar-title", children: [_jsxs("header", { className: "collection-chat-sidebar__header", children: [_jsxs("div", { className: "collection-chat-sidebar__header-text", children: [_jsxs("div", { className: "collection-chat-sidebar__heading-row", children: [_jsx("h2", { id: "collection-chat-sidebar-title", className: "collection-chat-sidebar__heading", children: "Ask about this collection" }), _jsx("span", { className: "collection-chat-sidebar__experimental-badge", title: "This feature is experimental and may change or break.", children: "Experimental" })] }), _jsx("p", { className: "collection-chat-sidebar__subheading", title: collectionTitle, children: collectionTitle })] }), _jsxs("div", { className: "collection-chat-sidebar__header-actions", children: [_jsx("button", { type: "button", className: "collection-chat-sidebar__header-button", onClick: handleClear, disabled: canClear === false, "aria-label": "Clear conversation", title: "Clear conversation", children: _jsx(FaIcon, { icon: faTrashCan }) }), _jsx("button", { type: "button", className: "collection-chat-sidebar__header-button", onClick: onClose, "aria-label": "Close", title: "Close", children: _jsx(FaIcon, { icon: faXmark }) })] })] }), _jsxs("div", { className: "collection-chat-sidebar__transcript", ref: transcriptRef, children: [messages.length === 0 && isSending === false && (_jsx("div", { className: "collection-chat-sidebar__empty", children: _jsxs("p", { children: ["Loaded the blocks in \u201C", collectionTitle, "\u201D. Ask anything \u2014 what's in it, what stood out, what you said about something."] }) })), messages.map((message) => (_jsx(ChatBubble, { message: message }, message.id))), isSending && (_jsxs("div", { className: "collection-chat-sidebar__bubble collection-chat-sidebar__bubble--assistant collection-chat-sidebar__bubble--pending", children: [_jsx(FaIcon, { icon: faCircleNotch, spin: true }), _jsx("span", { children: "Thinking\u2026" })] }))] }), _jsxs("form", { className: "collection-chat-sidebar__composer", onSubmit: handleSubmit, children: [_jsx("textarea", { ref: textareaRef, className: "collection-chat-sidebar__input", placeholder: "Ask about this collection\u2026", value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: handleKeyDown, maxLength: 2000, rows: 2, disabled: isSending, "aria-label": "Message" }), _jsx("button", { type: "submit", className: "collection-chat-sidebar__send", disabled: draft.trim().length === 0 || isSending, "aria-label": "Send", children: _jsx(FaIcon, { icon: isSending ? faCircleNotch : faPaperPlaneTop, spin: isSending }) })] })] }));
});
// ---------------------------------------------------------------------------
// Chat bubble + referenced-block chips
// ---------------------------------------------------------------------------
const ChatBubble = memo(({ message }) => {
    return (_jsxs("div", { className: `collection-chat-sidebar__bubble collection-chat-sidebar__bubble--${message.role}`, children: [_jsx("div", { className: "collection-chat-sidebar__bubble-content", children: message.content }), message.referencedBlockIds && message.referencedBlockIds.length > 0 && (_jsx("div", { className: "collection-chat-sidebar__refs", children: message.referencedBlockIds.map((id) => (_jsx(ReferencedBlockChip, { blockId: id }, id))) }))] }));
});
// Clicking a referenced block opens its detail modal on top of the page
// without closing the sidebar — the chat is now a persistent surface, so
// the user can pop a block, dismiss it, and keep the conversation going.
const ReferencedBlockChip = memo(({ blockId }) => {
    const app = useApp();
    const envelope = blockStore.useById(blockId);
    const title = envelope?.block.title ?? envelope?.block.summary?.slice(0, 80) ?? blockId;
    const typeLabel = envelope?.block.type ?? "block";
    const handleClick = useCallback(() => {
        app.setActiveBlock(blockId);
    }, [app, blockId]);
    return (_jsxs("button", { type: "button", className: "collection-chat-sidebar__ref-chip", onClick: handleClick, title: title, children: [_jsx("span", { className: "collection-chat-sidebar__ref-chip-type", children: typeLabel }), _jsx("span", { className: "collection-chat-sidebar__ref-chip-title", children: title })] }));
});
